import { type ProductId, webIAPPrices } from 'src/replicant/iap/iap.ruleset';
import StateObserver from 'src/StateObserver';
import { GCInstant } from 'src/lib/gcinstant';
import { captureGenericError } from 'src/lib/sentry';
import type {
  RevenueAnalytics,
  RevenuePredictionAnalytics,
} from './analytics.types';
import type { PurchaseMethod } from '../iap.types';
import {
  PurchaseProviderCreditCardBrand,
  RevenueProperties,
} from '@play-co/replicant';

/**
 * Get price in local currency for a given product,
 * It should return store(apple and google) base price, in case BD JPY
 */
export function getRevenueBasePrice(productID: ProductId): {
  price: number;
  currency: string;
} {
  if (!(productID in webIAPPrices)) {
    throw new Error(`Product ID ${productID} not found in webIAPPrices`);
  }
  return {
    price: webIAPPrices[productID], // JPY price
    currency: StateObserver.replicant.extras.getRevenueCurrency() ?? 'JPY',
  };
}

/**
 * Calculates the revenue gross analytics for a given purchase.
 *
 * @param purchase - The purchase details including currency, price, provider, and provider method.
 * @returns An object containing the revenue gross analytics including currency, dollar to local rate,
 *          revenue gross in local currency, and revenue gross in USD.
 *
 * @remarks
 * This function retrieves the revenue properties for the purchase from the replicant. If the data is not available,
 * it captures a generic error and returns default values.
 */
export function getRevenueGrossAnalytics(
  productID: ProductId,
  purchase: PurchaseMethod & {
    currency: string;
    price: number;
  },
): RevenueAnalytics {
  // Apple and google store base price
  const basePrice = getRevenueBasePrice(productID);

  // Detect credit card brand
  let creditCardBrand: PurchaseProviderCreditCardBrand | undefined = undefined;
  if ('providerCreditCardBrand' in purchase) {
    creditCardBrand = purchase.providerCreditCardBrand;
  }

  // Get the revenue properties for the purchase from replicant
  const data = getRevenue(purchase);

  // If we don't have the data, we can't calculate the revenue gross
  if (!data) {
    // Replicant offline doesn't support `getRevenueProperties` method.  Skip reporting error in this case to reduce noise.
    // This happens in IOS guest mode or local development.
    if (!process.env.REPLICANT_OFFLINE) {
      captureGenericError(
        `Unsupported purchase provider or currency: ${purchase.provider} ${purchase.providerMethod} currency: ${purchase.currency}`,
        null,
      );
    }

    // Fallback result with default values
    return {
      productID,
      currency: purchase.currency,
      productPrice: purchase.price,
      dollarToLocalRate: 0,
      revenueGrossLocal: 0,
      revenueGross: 0,
      revenueNet: 0,
      purchaseProvider: purchase.provider,
      purchaseProviderMethod: purchase.providerMethod,
      currencyCodeLocal: basePrice.currency,
    };
  }

  // Full result with all the data
  return {
    productID,
    currency: purchase.currency,
    productPrice: purchase.price,
    dollarToLocalRate: data.dollarToLocalRate ?? 0,
    revenueGrossLocal: data.revenueGrossLocal ?? 0,
    revenueGross: data.revenueGross,
    revenueNet: data.revenueNet,
    purchaseProvider: purchase.provider,
    purchaseProviderMethod: purchase.providerMethod,
    currencyCodeLocal: data.currencyCodeLocal,
  };
}

/**
 * Predict revenue gross based on platform.
 * We can full predict it only for native purchases.
 * For Open Web we can only predict gross revenue in JPY,
 * but not Net revenue because for exact calculation we need to know the payment method.
 */
export function predictRevenueGrossAnalytics(
  productID: ProductId,
): RevenuePredictionAnalytics {
  const osType = GCInstant.osType;
  // Apple is based on JPY price but actual charge from user could be different, so we should use platform revenue gross calculation, Here we just predict it based on JPY price
  // For Android: TODO: Check what is base currency for Android
  // For Open Web: We can calculate exact it based on JPY price
  const basePrice = getRevenueBasePrice(productID);

  switch (osType) {
    case 'ANDROID_APP':
      return getRevenueGrossAnalytics(productID, {
        ...basePrice,
        provider: 'google',
        providerMethod: 'google_native',
      });
    case 'IOS_APP':
      return getRevenueGrossAnalytics(productID, {
        ...basePrice,
        provider: 'apple',
        providerMethod: 'apple_native',
      });
    default:
      return {
        ...getRevenueGrossAnalytics(productID, {
          ...basePrice,
          provider: 'komoju', // Any provider will work here
          providerMethod: 'komoju_paypay', // We ned just gross revenue dollarToLocalRate
        }),
        purchaseProvider: undefined, // We can't know this
        purchaseProviderMethod: undefined, // We can't know this
        revenueNet: 0, // We do not know it for sure
      };
  }
}

/**
 * Calculates the revenue properties for a given purchase.
 *
 * @param purchase - The purchase details including currency and price.
 * @returns The revenue properties for the purchase or undefined if not applicable.
 *
 * @remarks
 * This function handles a special case for Komoju credit card purchases.
 * If the purchase provider is 'komoju' and the provider method is 'komoju_credit_card',
 * it retrieves the revenue properties including the credit card brand.
 * For other purchases, it retrieves the revenue properties without the credit card brand.
 *
 * @example
 * ```typescript
 * const purchase = {
 *   provider: 'komoju',
 *   providerMethod: 'komoju_credit_card',
 *   currency: JPY',
 *   price: 1000,
 *   providerCreditCardBrand: 'visa'
 * };
 * const revenue = getRevenue(purchase);
 * console.log(revenue);
 * ```
 */
function getRevenue(
  purchase: PurchaseMethod & {
    currency: string;
    price: number;
  },
): RevenueProperties | undefined {
  // Get the revenue properties for the purchase from replicant for other cases
  return StateObserver.replicant.extras.getRevenueProperties({
    currency: purchase.currency,
    amount: purchase.price,
    provider: purchase.provider,
    providerMethod: purchase.providerMethod,
    providerCreditCardBrand:
      purchase.providerMethod === 'komoju_credit_card'
        ? purchase.providerCreditCardBrand
        : undefined,
  });
}
